/**
 * Serviço BFF (Backend for Frontend) para Recommendation Engine
 * Implementa o padrão de desacoplamento: Frontend → Data-Provider → Frontend → Recommendation-Engine
 */

import appConfig from '../config/app-config';

interface ProviderData {
  status: 'SUCCESS' | 'ERROR' | 'TIMEOUT';
  data?: any;
  error_message?: string;
}

interface AnalysisRequest {
  social_id: string;
  document_type: 'CPF' | 'CNPJ';
  business_area: 'PLD' | 'CREDIT' | 'COMPLIANCE';
  provider_data: Record<string, ProviderData>;
  additional_data?: Record<string, any>;
}

interface AnalysisResponse {
  analysis_id: string;
  social_id: string;
  recommendation: 'APROVADO' | 'REPROVADO' | 'PENDENTE';
  risk_score: number;
  analysis_date: string;
  rules_applied: string[];
  message: string;
}

/**
 * AF-3418: ganchos para preenchimento progressivo da tela.
 *
 * A ordem de `batchData.reports` continua sendo montada por número de página — ela define
 * a correlação com os resultados da análise e não pode depender da ordem de chegada.
 * Estes callbacks servem só para exibição antecipada.
 */
export interface AnalyzeBatchHooks {
  /** Dispara a cada página de reports recebida do data-provider. */
  onReportsPage?: (page: { reports: any[]; loaded: number; total: number | null }) => void;
  /** Dispara a cada chunk analisado, com os reports já enriquecidos até o momento. */
  onPartialResults?: (reports: any[]) => void;
}

class RecommendationBFFService {
  private readonly DATA_PROVIDER_BASE_URL: string;
  private readonly DATA_PROVIDER_API_KEY: string;
  private readonly RECOMMENDATION_ENGINE_BASE_URL: string;
  private readonly RECOMMENDATION_ENGINE_API_KEY: string;

  constructor() {
    const dpEnv = appConfig.apis.dataProviderApiEnv;
    const reEnv = appConfig.apis.recommendationEngineApiEnv;
    this.DATA_PROVIDER_BASE_URL = dpEnv.address;
    this.DATA_PROVIDER_API_KEY = dpEnv.key;
    this.RECOMMENDATION_ENGINE_BASE_URL = reEnv.address;
    this.RECOMMENDATION_ENGINE_API_KEY = reEnv.key;
  }

  /**
   * Fluxo completo BFF: busca dados + análise de recomendação
   * A unificação por documento é feita apenas no frontend da tela de recomendações
   */
  async analyzeBatch(
    batchId: string,
    onProgress?: (processed: number, total: number) => void,
    hooks?: AnalyzeBatchHooks
  ): Promise<any> {
    try {
      // Buscar dados do lote no data-provider (já inclui reports processados).
      // AF-3418: onReportsPage permite a tela renderizar as linhas conforme as páginas
      // chegam. As medições mostraram que a paginação é onde o tempo se concentra (82s
      // para 46 páginas), então é o ponto que precisa dar retorno visual — não a análise,
      // que hoje leva ~3s para o lote inteiro.
      const batchData = await this.fetchBatchData(batchId, hooks?.onReportsPage);

      // Preparar payload batch com todos os documentos
      if (batchData.reports && Array.isArray(batchData.reports)) {
        let documents: any[];
        
        // ✅ ESCAVADOR: Enviar 1 documento por processo (não agrupar)
        if (batchData.provider === 'ESCAVADOR') {
          documents = batchData.reports.map((report: any) => {
            const document = report.document || report.social_id;
            
            const excludeFields = ['document', 'name', 'status', 'statusFormatted', 'createdAt', 'updatedAt',
              'backend_recommendation', 'backend_reasons', 'backend_risk_score',
              'backend_analysis_id', 'backend_document_type', 'reportType', 'reportTypeId',
              'batchReportId', 'attempts', 'maxAttempts', 'events', 'analysis_status', 'pep_reason'];
            
            const reportData: any = {};
            Object.keys(report).forEach(key => {
              if (!excludeFields.includes(key)) {
                reportData[key] = report[key];
              }
            });
            
            return {
              social_id: document,
              document_type: document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ',
              business_area: 'PLD',
              process_id: report.numero_processo || '',  // ✅ Identificador único do processo
              provider_data: {
                ESCAVADOR: {
                  status: 'SUCCESS',
                  data: reportData  // ✅ Dados do processo específico
                }
              }
            };
          });
          
          console.log(`✅ ESCAVADOR: ${documents.length} processos individuais para análise`);
          console.log(`📤 Exemplo de documento enviado:`, documents[0]);
          console.log(`📋 Process IDs enviados:`, documents.slice(0, 5).map((d: any) => `${d.social_id} → "${d.process_id}"`));
        } else {
          // ✅ INOVAMIND/QUOD: Manter lógica atual (1 report = 1 documento)
          documents = batchData.reports.map((report: any) => {
            const providerData: Record<string, ProviderData> = {};
            const excludeFields = ['document', 'name', 'status', 'statusFormatted', 'createdAt', 'updatedAt',
              'backend_recommendation', 'backend_reasons', 'backend_risk_score',
              'backend_analysis_id', 'backend_document_type', 'reportType', 'reportTypeId',
              'batchReportId', 'attempts', 'maxAttempts', 'events', 'analysis_status', 'pep_reason'];
            const reportData: any = {};
            Object.keys(report).forEach(key => {
              if (!excludeFields.includes(key)) {
                reportData[key] = report[key];
              }
            });
            const providerName = batchData.provider || 'UNKNOWN';
            providerData[providerName] = {
              status: 'SUCCESS',
              data: reportData
            };
            const document = report.document || report.social_id;
            return {
              social_id: document,
              document_type: document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ',
              business_area: 'PLD',
              provider_data: providerData
            };
          });
        }

        // Enviar documentos com chunking para evitar timeout
        const batchResults = await this.sendBatchToRecommendationEngineWithChunking(
          documents,
          200,
          (processed, total) => {
            // Callback externo (UI)
            if (onProgress) {
              onProgress(processed, total);
            }
            // AF-3418: avisa a tela a cada chunk concluído para ela re-renderizar as
            // linhas já com recomendação. O enriquecimento definitivo continua acontecendo
            // na passada única abaixo, que é idempotente — este gancho só antecipa a
            // exibição, não substitui a correlação.
            if (hooks?.onPartialResults) {
              hooks.onPartialResults(batchData.reports);
            }
            // Log de progresso reduzido
            if (processed % 50 === 0 || processed === total) {
              console.info(`[RecommendationBFFService] Progresso: ${processed}/${total} (${Math.round(processed / total * 100)}%)`);
            }
          }
        );

        // Enriquecer reports com resultados
        if (batchData.provider === 'ESCAVADOR') {
          // ESCAVADOR: Mapear por process_id (1:1 com resultado)
          console.log(`📊 ESCAVADOR: ${batchData.reports.length} reports, ${batchResults.results.length} resultados`);
          console.log(`📊 Primeiro resultado:`, batchResults.results[0]);
          
          batchData.reports.forEach((report: any) => {
            // 🎯 Estratégia de matching com fallback
            let result = null;
            
            // 1ª tentativa: Match exato (social_id + process_id)
            if (report.numero_processo) {
              result = batchResults.results.find((r: any) => 
                r.social_id === report.document && 
                r.process_id === report.numero_processo
              );
              if (result) {
                console.log(`✅ Match exato: ${report.document} + processo ${report.numero_processo}`);
              }
            }
            
            // 2ª tentativa: Fallback por social_id apenas (quando process_id vazio ou não encontrado)
            if (!result) {
              result = batchResults.results.find((r: any) => 
                r.social_id === report.document
              );
              if (result) {
                console.log(`⚠️ Match por fallback (social_id): ${report.document} → ${result.recommendation}`);
              }
            }
            
            if (result) {
              report.backend_recommendation = result.recommendation;
              report.backend_reasons = result.rules_applied;
              report.backend_risk_score = result.risk_score;
              report.backend_analysis_id = result.analysis_id;
              report.backend_credit_flag = result.credit_flag;
              report.backend_pld_flag = result.pld_flag;
              if (result.error) {
                report.backend_error = result.error;
              }
            } else {
              // AF-3418: antes só logava e deixava o report sem recomendação, o que a tela
              // exibia como "Pendente". Sem resultado é ERRO explícito.
              console.error(`❌ ESCAVADOR: Nenhum resultado encontrado para ${report.document}`);
              console.error(`   process_id enviado: "${report.numero_processo}"`);
              report.backend_recommendation = 'ERRO';
              report.backend_reasons = [];
              report.backend_error = 'Sem resultado retornado pelo motor de regras';
            }
          });
        } else {
          // INOVAMIND/QUOD: 1 report = 1 documento, correlação posicional.
          // AF-3418: a posição agora é garantida por sendBatchToRecommendationEngineWithChunking,
          // mas conferimos o social_id como rede de segurança. `documents` foi construído com
          // reports.map(...), então documents[i] corresponde a reports[i] — se o documento não
          // casar, houve desalinhamento e marcamos como ERRO em vez de exibir dado de outro CNPJ.
          let mismatches = 0;
          batchData.reports.forEach((report: any, index: number) => {
            const result = batchResults.results[index];
            const reportDocument = report.document || report.social_id;

            if (!result) {
              report.backend_recommendation = 'ERRO';
              report.backend_reasons = [];
              report.backend_error = 'Sem resultado retornado pelo motor de regras';
              mismatches++;
              return;
            }

            if (result.social_id && reportDocument && result.social_id !== reportDocument) {
              mismatches++;
              console.error(
                `[AF-3418] Desalinhamento detectado na posição ${index}: resultado do documento ` +
                `${result.social_id} chegou na posição do documento ${reportDocument}. ` +
                `Marcando como ERRO.`
              );
              report.backend_recommendation = 'ERRO';
              report.backend_reasons = [];
              report.backend_error = 'Desalinhamento entre documento e resultado da análise';
              return;
            }

            report.backend_recommendation = result.recommendation;
            report.backend_reasons = result.rules_applied;
            report.backend_risk_score = result.risk_score;
            report.backend_analysis_id = result.analysis_id;
            report.backend_credit_flag = result.credit_flag;
            report.backend_pld_flag = result.pld_flag;
            if (result.error) {
              report.backend_error = result.error;
            }
          });

          if (mismatches > 0) {
            console.error(
              `[AF-3418] ${mismatches}/${batchData.reports.length} reports do provider ` +
              `${batchData.provider} ficaram sem recomendação confiável.`
            );
          }
        }
      }
      return batchData;

    } catch (error) {
      console.error('❌ Erro na análise batch do lote:', error);
      throw error;
    }
  }

  /**
   * Envia lote completo para recommendation-engine (1 única requisição)
   * Método público para uso em telas que precisam análise batch direta
   * Implementa retry automático com backoff linear
   */
  async sendBatchToRecommendationEngine(documents: any[], maxRetries: number = 3): Promise<any> {
    const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/recommendations/analyze-batch`;

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      console.log(`🔄 [Batch] Tentativa ${attempt}/${maxRetries} - ${documents.length} documentos`);

      // Timeout de 120 segundos para processar lote grande
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      try {
        const authToken = localStorage.getItem('authToken');
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'x-api-key': this.RECOMMENDATION_ENGINE_API_KEY,
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
          },
          body: JSON.stringify({ documents }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ [Batch] Erro na tentativa ${attempt}:`, errorText);
          
          if (attempt < maxRetries) {
            const delay = 1000 * attempt; // 1s, 2s, 3s
            console.log(`⏳ [Batch] Aguardando ${delay}ms antes de retry...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          
          throw new Error(`Batch analysis error: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const result = await response.json();
        
        // Backend retorna status 207 se houver falhas parciais
        if (result.failures && result.failures.length > 0) {
          console.warn(`⚠️ [Batch] ${result.failures.length} documentos falharam:`, result.failures);
        }
        
        console.log(`✅ [Batch] Processado: ${result.success} sucessos, ${result.errors} erros`);
        return result;

      } catch (error) {
        clearTimeout(timeoutId);
        lastError = error as Error;
        
        if (error instanceof Error && error.name === 'AbortError') {
          console.error(`❌ [Batch] Timeout na tentativa ${attempt}`);
          if (attempt < maxRetries) {
            const delay = 2000 * attempt; // 2s, 4s, 6s para timeout
            console.log(`⏳ [Batch] Aguardando ${delay}ms antes de retry...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          throw new Error('Timeout ao processar batch (120s). Tente com menos documentos ou aguarde.');
        }
        
        console.error(`❌ [Batch] Erro na tentativa ${attempt}:`, error);
        if (attempt < maxRetries) {
          const delay = 1000 * attempt;
          console.log(`⏳ [Batch] Aguardando ${delay}ms antes de retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    console.error('❌ [Batch] Todas as tentativas falharam');
    throw lastError || new Error('Failed to process batch after retries');
  }

  /**
   * Envia lote grande em chunks para evitar timeout do API Gateway (29s)
   * Processa N documentos por vez com progresso
   */
  async sendBatchToRecommendationEngineWithChunking(
    documents: any[],
    chunkSize: number = 200,
    onProgress?: (processed: number, total: number) => void,
    concurrency: number = 4
  ): Promise<any> {
    const totalChunks = Math.ceil(documents.length / chunkSize);

    // Dividir documentos em chunks
    const chunks: any[][] = [];
    for (let i = 0; i < documents.length; i += chunkSize) {
      chunks.push(documents.slice(i, i + chunkSize));
    }

    // ⚠️ AF-3418 — CAUSA RAIZ DO BUG DE RECOMENDAÇÃO TROCADA.
    // Antes: `allResults.push(...chunkResult.results)` dentro de processChunk. Com os
    // chunks rodando em paralelo, o array era montado na ordem de CONCLUSÃO, não de envio.
    // Como o chamador correlaciona resultado↔documento por posição, um lote de 661 docs
    // (chunks de 200/200/200/61) tinha o array inteiro embaralhado: o chunk de 61 terminava
    // primeiro e suas recomendações eram atribuídas aos 61 PRIMEIROS documentos.
    // Confirmado nos logs de produção (chunk de 61 concluiu 2,6s antes dos de 200).
    // Agora: cada chunk escreve num slot fixo e o array final é montado em ordem de envio.
    const resultsByChunk: any[][] = new Array(chunks.length);

    const errorPlaceholder = (doc: any, index: number, reason: string) => ({
      index,
      social_id: doc.social_id,
      process_id: doc.process_id,
      document_type: doc.document_type,
      recommendation: 'ERRO',
      rules_applied: [],
      risk_score: 0,
      pld_flag: false,
      credit_flag: false,
      error: reason
    });

    let processedCount = 0;
    const processChunk = async (chunk: any[], chunkNumber: number) => {
      try {
        const chunkResult = await this.sendBatchToRecommendationEngine(chunk);

        // AF-3418: reposicionar cada resultado pelo `index` devolvido pelo backend
        // (posição no payload do chunk). Não confiamos mais na ordem do array.
        const ordered: any[] = new Array(chunk.length).fill(null);
        for (const result of (chunkResult.results || [])) {
          const idx = result?.index;
          if (typeof idx === 'number' && idx >= 0 && idx < chunk.length) {
            ordered[idx] = result;
          }
        }

        // Backend legado (sem `index`): só é seguro usar a ordem se o tamanho casar.
        const hasIndex = (chunkResult.results || []).some((r: any) => typeof r?.index === 'number');
        if (!hasIndex) {
          if ((chunkResult.results || []).length === chunk.length) {
            for (let i = 0; i < chunk.length; i++) ordered[i] = chunkResult.results[i];
          } else {
            console.error(
              `[RecommendationBFFService] Chunk ${chunkNumber + 1}/${totalChunks}: resposta sem ` +
              `'index' e com tamanho divergente (${(chunkResult.results || []).length} != ${chunk.length}). ` +
              `Marcando o chunk como erro para não exibir recomendação de outro documento.`
            );
          }
        }

        // Qualquer posição sem resultado vira ERRO explícito (nunca herda de outro doc)
        for (let i = 0; i < chunk.length; i++) {
          if (!ordered[i]) {
            ordered[i] = errorPlaceholder(chunk[i], i, 'Sem resultado retornado pelo motor de regras');
          }
        }

        resultsByChunk[chunkNumber] = ordered;
        processedCount += chunk.length;
        if (onProgress) {
          onProgress(processedCount, documents.length);
        }
      } catch (error) {
        console.error(`[RecommendationBFFService] Erro no chunk ${chunkNumber + 1}/${totalChunks}:`, error);
        resultsByChunk[chunkNumber] = chunk.map((doc, i) =>
          errorPlaceholder(doc, i, 'Chunk processing failed')
        );
        processedCount += chunk.length;
        if (onProgress) {
          onProgress(processedCount, documents.length);
        }
      }
    };

    // Processar chunks em batches de concurrency
    const promises: Promise<void>[] = [];
    for (let i = 0; i < chunks.length; i++) {
      promises.push(processChunk(chunks[i], i));

      // Aguarda quando atingir concorrência máxima ou último chunk
      if (promises.length >= concurrency || i === chunks.length - 1) {
        await Promise.all(promises);
        promises.length = 0;  // Limpa array para próximo batch
      }
    }

    // AF-3418: concatenação em ordem de ENVIO (índice do chunk), não de conclusão
    const allResults: any[] = [];
    for (let c = 0; c < chunks.length; c++) {
      allResults.push(...(resultsByChunk[c] || []));
    }

    // Invariante: 1 resultado por documento, na mesma ordem
    if (allResults.length !== documents.length) {
      throw new Error(
        `[AF-3418] Inconsistência na análise em lote: ${allResults.length} resultados para ` +
        `${documents.length} documentos. Abortando para não exibir recomendação incorreta.`
      );
    }

    // AF-3418: contagem de erros derivada do array final, num único lugar.
    //
    // Antes era acumulada em dois pontos: +1 por posição sem resultado E o contador
    // `errors`/`total_errors` da resposta. Com o backend legado, que omite a falha de
    // `results` e ao mesmo tempo informa `errors: 1`, a mesma falha era contada duas vezes.
    // Derivar do resultado montado dá uma fonte única de verdade, funciona igual para o
    // backend legado e o novo (que já devolve placeholder de ERRO em cada posição), e
    // captura também o documento que voltou como ERRO sem ter entrado em `failures` —
    // caso do fail-closed quando nenhum provider pôde ser avaliado.
    const totalErrors = allResults.filter(result => result?.recommendation === 'ERRO').length;

    console.log(`✅ Processamento completo: ${allResults.length}/${documents.length} processados, ${totalErrors} erros`);

    return {
      results: allResults,
      total_processed: documents.length,
      total_errors: totalErrors
    };
  }

  /**
   * Busca dados de um lote específico (todas as páginas)
   */
  async fetchBatchData(
    batchId: string,
    onPage?: (page: { reports: any[]; loaded: number; total: number | null }) => void
  ): Promise<any> {
    const baseUrl = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/${batchId}/page-view`;
    const authToken = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'x-api-key': this.DATA_PROVIDER_API_KEY,
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
    };

    // Primeira página para obter metadata
    const firstRes = await fetch(`${baseUrl}?page=0&pageSize=100`, { headers });
    if (!firstRes.ok) throw new Error(`Batch data error: ${firstRes.status} ${firstRes.statusText}`);
    const firstData = await firstRes.json();
    if (firstData.error) throw new Error(`API error: ${firstData.error}`);

    console.log('📦 Dados do lote retornados:', firstData);

    const totalPages = firstData.totalPages || 1;

    // AF-3418: páginas restantes em paralelo (antes era um for sequencial com await).
    // Medição em produção: a API do data-provider responde em ~1,6s (p50) / 7,2s (p90) por
    // página de 100. Num lote ESCAVADOR de ~9.600 processos são ~96 páginas em série, ou
    // seja, minutos só de paginação antes de qualquer regra rodar.
    // A ordem final é montada por número de página, não por ordem de chegada — a ordem de
    // `reports` define a correlação com os resultados da análise.
    const pagesByIndex: any[][] = new Array(totalPages);
    pagesByIndex[0] = firstData.reports || [];
    let failedPages = 0;
    let loadedCount = pagesByIndex[0].length;
    const totalElements = firstData.totalElements ?? null;

    // Primeira página já disponível para a tela
    if (onPage) {
      onPage({ reports: pagesByIndex[0], loaded: loadedCount, total: totalElements });
    }

    if (totalPages > 1) {
      console.log(`📄 Lote com ${totalPages} páginas, buscando restantes em paralelo...`);

      const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 1);
      const concurrency = 8;

      const fetchPage = async (page: number): Promise<boolean> => {
        try {
          const res = await fetch(`${baseUrl}?page=${page}&pageSize=100`, { headers });
          if (!res.ok) {
            pagesByIndex[page] = [];
            console.error(`❌ Página ${page + 1}/${totalPages} falhou: ${res.status}`);
            return false;
          }
          const pageData = await res.json();
          pagesByIndex[page] = pageData.reports || [];
          // AF-3418: entrega a página para exibição imediata. O array final continua sendo
          // montado por número de página mais adiante — este callback não define ordem.
          loadedCount += pagesByIndex[page].length;
          if (onPage) {
            onPage({ reports: pagesByIndex[page], loaded: loadedCount, total: totalElements });
          }
          return true;
        } catch (error) {
          pagesByIndex[page] = [];
          console.error(`❌ Página ${page + 1}/${totalPages} falhou:`, error);
          return false;
        }
      };

      for (let i = 0; i < pageNumbers.length; i += concurrency) {
        const outcomes = await Promise.all(
          pageNumbers.slice(i, i + concurrency).map(fetchPage)
        );
        failedPages += outcomes.filter(ok => !ok).length;
      }
    }

    const allReports: any[] = [];
    for (let page = 0; page < totalPages; page++) {
      allReports.push(...(pagesByIndex[page] || []));
    }

    if (failedPages > 0) {
      // AF-3418: dado incompleto não pode passar silenciosamente — a tela mostraria menos
      // documentos do que o lote realmente tem e o parecer seria salvo incompleto.
      throw new Error(
        `Falha ao carregar ${failedPages} de ${totalPages} páginas do lote ${batchId}. ` +
        `Dados incompletos — recarregue a tela.`
      );
    }

    console.log(`📦 Total de reports carregados: ${allReports.length}`);
    firstData.reports = allReports;
    return firstData;
  }

  /**
   * Lista todos os lotes disponíveis (usa data-provider diretamente)
   */
  async fetchBatchList(): Promise<any> {
    // Para listagem de lotes, usar data-provider diretamente é correto
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/page-list?page=0&pageSize=200&excludePrefix=CAE-`;

    const authToken = localStorage.getItem('authToken');
    const response = await fetch(url, {
      headers: {
        'x-api-key': this.DATA_PROVIDER_API_KEY,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Batch list error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Lista lotes com filtros (compatível com PageList)
   */
  async fetchBatchListWithFilters(params: URLSearchParams): Promise<any> {
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/page-list?${params.toString()}`;

    const authToken = localStorage.getItem('authToken');
    const response = await fetch(url, {
      headers: {
        'x-api-key': this.DATA_PROVIDER_API_KEY,
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
      }
    });

    if (!response.ok) {
      throw new Error(`Batch list error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Busca último parecer salvo para um batch
   */
  /**
   * Salva parecer PLD consolidado
   */
  async savePLDReport(parecerData: any): Promise<any> {
    const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/pld-reports`;
    
    console.log('💾 [BFF] Salvando parecer PLD...');
    console.log('📦 [BFF] Dados do parecer:', parecerData);
    console.log('🌐 [BFF] URL:', url);
    
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': this.RECOMMENDATION_ENGINE_API_KEY,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify(parecerData)
    });

    console.log('📡 [BFF] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [BFF] Erro ao salvar:', errorText);
      throw new Error(`Save PLD report error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ [BFF] Parecer salvo com sucesso:', result);
    return result;
  }

  async sendPLDNotifications(batchSk: string, parecerData: any): Promise<any> {
    const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/pld-reports/${batchSk}/notify`;

    const authToken = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': this.RECOMMENDATION_ENGINE_API_KEY,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify(parecerData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Send notifications error: ${response.status} - ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Verifica se parecer PLD já existe para os lotes selecionados
   */
  async checkPLDReportExists(batchSelections: any): Promise<any> {
    const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/pld-reports/check`;
    
    console.log('🔍 [BFF] Verificando parecer existente...');
    console.log('📦 [BFF] batchSelections:', batchSelections);
    console.log('🌐 [BFF] URL:', url);
    
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': this.RECOMMENDATION_ENGINE_API_KEY,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ batchSelections })
    });

    console.log('📡 [BFF] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [BFF] Erro na resposta:', errorText);
      throw new Error(`Check PLD report error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('✅ [BFF] Resultado:', result);
    return result;
  }
}

// Instância singleton
export const recommendationBFFService = new RecommendationBFFService();

// Export da classe para testes
export { RecommendationBFFService };
